/** Nexus environments that must use temp onboarding BigQuery tables. */
export const ONBOARDING_BQ_NON_PRODUCTION_ENVIRONMENTS = ['development', 'test', 'staging'] as const;

export type OnboardingBqEnvironment = 'development' | 'test' | 'staging' | 'production';

export const ONBOARDING_BQ_TABLES_CANONICAL = {
    client_context: 'vertex-poc-447312.system.client_context',
    clients_params: 'vertex-poc-447312.run_management.clients_params',
    core_comp_map: 'vertex-poc-447312.system.core_comp_map',
} as const;

export const ONBOARDING_BQ_TABLES_TEMP = {
    client_context: 'vertex-poc-447312.system.temp_client_context',
    clients_params: 'vertex-poc-447312.run_management.temp_clients_params',
    core_comp_map: 'vertex-poc-447312.system.temp_core_comp_map',
} as const;

/**
 * `NEXUS_GCP_PROJECT_ID` of each production deployment, the only identities allowed to reach the
 * canonical tables. Each comes from a GitHub environment variable that overrides the
 * repository-level `vertex-poc-447312`: `production` (`deploy.yml`) sets `nexus-prod-v1` and
 * `production-v2` sets `nexus-prod-v2`. PR environments pin `nexus-environments`
 * (k8s/pr-env/configmap-template.yaml), so a PR environment that claims to be production is refused.
 */
export const ONBOARDING_BQ_PRODUCTION_PROJECT_IDS = ['nexus-prod-v1', 'nexus-prod-v2', 'vertex-poc-447312'] as const;

/** Where a service runs: `NEXUS_ENVIRONMENT` plus the deployment's `NEXUS_GCP_PROJECT_ID`. */
export interface OnboardingBqDeployment {
    environment: OnboardingBqEnvironment;
    gcpProjectId: string;
}

export type OnboardingBqTableKey = keyof typeof ONBOARDING_BQ_TABLES_CANONICAL;

export type OnboardingBqTableSet = Readonly<Record<OnboardingBqTableKey, string>>;

const CANONICAL_TABLES = new Set<string>(Object.values(ONBOARDING_BQ_TABLES_CANONICAL));
const TEMP_TABLES = new Set<string>(Object.values(ONBOARDING_BQ_TABLES_TEMP));

/**
 * One environment-specific set for all onboarding persistence DML and read-back.
 * Production uses canonical tables only; every other Nexus environment uses temp tables.
 */
export function resolveOnboardingBqTablesForEnvironment(environment: OnboardingBqEnvironment): OnboardingBqTableSet {
    if (environment === 'production') return ONBOARDING_BQ_TABLES_CANONICAL;
    if ((ONBOARDING_BQ_NON_PRODUCTION_ENVIRONMENTS as readonly OnboardingBqEnvironment[]).includes(environment)) {
        return ONBOARDING_BQ_TABLES_TEMP;
    }
    throw new Error(`Unsupported Nexus environment for onboarding BigQuery routing: ${String(environment)}`);
}

/**
 * The table set for a running deployment. Canonical tables also need a production project id,
 * so `NEXUS_ENVIRONMENT=production` alone (a misconfigured PR environment) fails closed.
 */
export function resolveOnboardingBqTablesForDeployment(deployment: OnboardingBqDeployment): OnboardingBqTableSet {
    const isProductionProject = (ONBOARDING_BQ_PRODUCTION_PROJECT_IDS as readonly string[]).includes(deployment.gcpProjectId);
    if (deployment.environment === 'production' && !isProductionProject) {
        throw new Error(
            `Onboarding refuses the canonical BigQuery tables: NEXUS_ENVIRONMENT is production but NEXUS_GCP_PROJECT_ID "${deployment.gcpProjectId}" is not a production deployment`,
        );
    }
    return resolveOnboardingBqTablesForEnvironment(deployment.environment);
}

/**
 * Whether this deployment may write onboarding and Brand Brain data at all. `null` means
 * NEXUS_ENVIRONMENT was not set; a deployment the routing above refuses (an unknown environment,
 * or production on a project that is not a production deployment) cannot write either.
 */
export function canWriteOnboarding(deployment: OnboardingBqDeployment | null): boolean {
    if (!deployment) return false;
    try {
        resolveOnboardingBqTablesForDeployment(deployment);
        return true;
    } catch {
        return false;
    }
}

/** Fail closed when a table id is not part of the resolved onboarding set for this environment. */
export function assertOnboardingBqTableForEnvironment(table: string, environment: OnboardingBqEnvironment): void {
    const allowed = resolveOnboardingBqTablesForEnvironment(environment);
    const allowedSet = new Set(Object.values(allowed));
    if (allowedSet.has(table)) return;

    if (environment === 'production' && TEMP_TABLES.has(table)) {
        throw new Error('Onboarding persistence cannot use temp BigQuery tables in production');
    }
    if (environment !== 'production' && CANONICAL_TABLES.has(table)) {
        throw new Error(`Onboarding persistence cannot use canonical BigQuery tables in ${environment}`);
    }
    throw new Error(`BigQuery table is not allowlisted for onboarding persistence: ${table}`);
}

export function isOnboardingClientContextTable(table: string, tables: OnboardingBqTableSet): boolean {
    return table === tables.client_context;
}

export function isOnboardingClientsParamsTable(table: string, tables: OnboardingBqTableSet): boolean {
    return table === tables.clients_params;
}
